import { inject, injectable } from 'inversify';

import {
  CreateProjectPayload,
  Page,
  ProjectBody,
  ProjectData,
  ProjectFont,
  ProjectPayload,
  PublicProjectBody,
  PublicProjectFont,
  UpdateProjectPayload,
} from '../models/project.model';
import { TYPES } from '../projects.types';
import { ProjectsRepository } from '../repositories/projects.repository';
import { ProjectStoreInjector } from '../store/projects.store';

@injectable()
export class ProjectsFacade {
  public constructor(
    @inject(TYPES.ProjectsRepository)
    private readonly repository: ProjectsRepository,
    @inject(TYPES.ProjectsStore)
    private readonly store: ProjectStoreInjector
  ) {}

  public async loadProject(
    id: string | number,
    type: ProjectPayload['type']
  ): Promise<ProjectBody> {
    const project = await this.repository.fetchProject(id, type);

    this.store.getState().project.setProject(project);

    return project;
  }

  public async loadPublicProject(
    id: string | number,
    type: ProjectPayload['type']
  ): Promise<PublicProjectBody> {
    const project = await this.repository.fetchPublicProject(id, type);

    this.store.getState().project.setProject(project);

    return project;
  }

  public async loadPage(pageId: string | number): Promise<Page> {
    const projectId = this.getProject()?.encoded_id;

    if (!projectId) {
      throw new Error('Project must be loaded before loading a page.');
    }

    const page = await this.repository.fetchPage(projectId, pageId);

    this.store.getState().pages.setPage(page);

    return page;
  }

  public async loadPublicPage(pageId: string | number): Promise<Page> {
    const projectId = this.getProject()?.encoded_id;

    if (!projectId) {
      throw new Error('Project must be loaded before loading a public page.');
    }

    const page = await this.repository.fetchPublicPage(projectId, pageId);

    this.store.getState().pages.setPage(page);

    return page;
  }

  public getProject(): ProjectBody | PublicProjectBody | null {
    return this.store.getState().project.currentProject;
  }

  public getPage(id: string | number): Page | undefined {
    return this.store.getState().pages.allPages[id];
  }

  public async loadFontData(fontId: number): Promise<ProjectFont> {
    const fonts = await this.repository.fetchFontData(fontId);

    if (!fonts) {
      throw new Error(`Fonts with ID ${fontId} not found`);
    }

    return fonts;
  }

  public async loadPublicProjectFonts(projectId: string | number): Promise<PublicProjectFont> {
    const fonts = await this.repository.fetchPublicProjectFonts(projectId);

    if (!fonts) {
      throw new Error(`Fonts with Project ID ${projectId} not found`);
    }

    return fonts;
  }

  public async createProject(payload: CreateProjectPayload): Promise<ProjectData> {
    const project = await this.repository.createProject(payload);

    return project;
  }

  public async updateProject(data: UpdateProjectPayload): Promise<ProjectData> {
    const existingProject = this.getProject() as ProjectBody;
    const currentProject = {
      ...existingProject,
      ...data,
    } as UpdateProjectPayload;

    const mergedProject = await this.repository.updateProject(currentProject, existingProject);

    this.store.getState().project.setProject(mergedProject as ProjectBody);

    return mergedProject;
  }
}
